import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import DownloadIcon from 'icons/Download.icon';
import EyeIcon from 'icons/Eye.icon';
import { DataRoomNodeReport } from '../../../Reports/types';
import utils, { capitalizeName, sentryError } from 'utils';
import { Loader } from 'ncoded-component-library';
import ReportsEmptyState from '../../../Reports/components/ReportsEmptyState';
import { isDataRoomViewer } from 'router/subrouters/DataRoom/utils';
import LockclosedIcon from 'icons/Lockclosed.icon';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import usePastTime from 'hooks/usePastTime';
import { FIRST_PAGE } from 'data/global';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';

import './RankedMembers.styles.scss';
import './RankedMembers.styles.responsive.scss';

type SortName =
  | 'numberOfViews'
  | 'numberOfDownloads'
  | 'numberOfSecondsSpent'
  | 'updatedAt';

const POPUP_CONTENT = {
  numberOfViews: 'numberOfViewsPopup',
  numberOfDownloads: 'numberOfDownloadsPopup',
  numberOfSecondsSpent: 'numberOfSecondsSpentPopup',
  updatedAt: 'updatedAtPopup',
};

const LIMIT = '10';

type RankedMembersProps = {
  className?: string;
};

const RankedMembers: React.FC<RankedMembersProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState<DataRoomNodeReport[]>([]);
  const [sort, setSort] = useState('-numberOfDownloads');

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const pastTime = usePastTime();

  const { t } = useTranslation();

  const classes = classNames(
    'ranked-members',
    { loading: isLoading },
    className,
  );

  const renderTrackedTime = useCallback(
    (seconds: number) => {
      const { type, count } = utils.timeSpentInSecondsHumanize(seconds);
      return t(`timeSpentInSecondsHumanize.${type}`, { count });
    },
    [t],
  );

  const renderSortButton = useCallback(
    (sortName: SortName, content?: ReactNode) => (
      <SortingButton
        content={content}
        sortingValue={sortName}
        popupContent={t(POPUP_CONTENT[sortName])}
        sort={sort}
        updateSort={setSort}
      />
    ),
    [sort, t],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { items },
        } = await dataRoomApi.reports.getReportByMemberActivity(dataRoomId, {
          $page: FIRST_PAGE,
          $limit: LIMIT,
          $sort: sort,
        });
        setReports(items);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dataRoomId, sort]);

  return (
    <div className={classes}>
      <div className="header">
        <p>{t('topRankedMember')}</p>
      </div>

      {isLoading ? (
        <Loader overlay={false} />
      ) : !reports.length ? (
        <ReportsEmptyState />
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t('member')}</th>
              <th>{renderSortButton('numberOfDownloads', t('downloads'))}</th>
              <th>{renderSortButton('numberOfViews', t('views'))}</th>
              <th>{renderSortButton('numberOfSecondsSpent', t('time'))}</th>
              <th>{renderSortButton('updatedAt', t('recent'))}</th>
            </tr>
          </thead>
          <tbody>
            {reports.map(
              ({
                _id,
                dataRoomMember,
                numberOfDownloads,
                numberOfViews,
                numberOfSecondsSpent,
                updatedAt,
              }) => (
                <tr key={_id}>
                  <td>
                    <div className="ranked-members__name">
                      <span>{capitalizeName(dataRoomMember?.fullName)}</span>
                    </div>
                  </td>
                  <td
                    className={classNames('downloads', {
                      selected: sort === 'numberOfDownloads',
                    })}
                  >
                    <div>
                      {isDataRoomViewer(dataRoomMember?.role) ? (
                        <LockclosedIcon />
                      ) : (
                        <>
                          {numberOfDownloads}
                          <DownloadIcon />
                        </>
                      )}
                    </div>
                  </td>
                  <td
                    className={classNames({
                      selected: sort === 'numberOfViews',
                    })}
                  >
                    <div>
                      {numberOfViews}
                      <EyeIcon />
                    </div>
                  </td>
                  <td
                    className={classNames({
                      selected: sort === 'numberOfSecondsSpent',
                    })}
                  >
                    <div>{renderTrackedTime(numberOfSecondsSpent)}</div>
                  </td>
                  <td
                    className={classNames({
                      selected: sort === 'updatedAt',
                    })}
                  >
                    <span>{pastTime(updatedAt)}</span>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RankedMembers;
